import React, { useState, useContext, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, Tooltip, Collapse, FormControlLabel, Switch, Checkbox, IconButton, Paper, Popover, Dialog, Button } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { usePlannerTheme } from '../../theme/plannerTheme';
import TuneIcon from '@material-ui/icons/Tune';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import HomeIcon from '@material-ui/icons/Home';
import SaveIcon from '@material-ui/icons/Save';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import DirectionsIcon from '@material-ui/icons/Directions';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import SecurityIcon from '@material-ui/icons/Security';
import HotelIcon from '@material-ui/icons/Hotel';
import RoomIcon from '@material-ui/icons/Room';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import { AuthContext } from '../../App';
import { isMoveExpressTenant } from '../../utils/manageHelpers';
import {
  selectCurrentRoute, setRouteOptions, setFuelType, setFuelPrice, addWaypoint,
  addRouteMarker, updateRouteMarker, removeRouteMarker,
  saveRouteToAPI, selectSaving, setRouteName, clearRoute,
  setReplanningRouteId, fetchSavedRoutes, selectActualTrail,
} from '../../store/slices/routesSlice';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { selectDrivers } from '../../store/slices/driversSlice';
import SaveRouteDialog from '../common/SaveRouteDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import SendIcon from '@material-ui/icons/Send';
import CloseIcon from '@material-ui/icons/Close';
import { setNeedsRecalculation, setMapClickMode, setHoveredMarkerId, setHoveredSavedLocation, setSidebarTab, toggleDetailsPanel, selectUI, navigateTo, setActiveView, setShowDetails, setSidebarVisible, setResetMapView, setDisplayedSavedRoutes } from '../../store/slices/uiSlice';
import { selectLocations, fetchLocations, createLocation } from '../../store/slices/locationsSlice';
import { addToast } from '../../store/slices/notificationsSlice';
import { t } from '../../i18n';
import ModuleGate from '../common/ModuleGate';
import { CURRENCIES, getCurrencySymbol, getSavedFuelPrice, saveFuelPrice } from '../../utils/currencies';
import { URBAN_AREAS_BY_COUNTRY } from '../../utils/hereApi';
import { reverseGeocode } from '../../utils/mapboxApi';

// ─── Marker types config ────────────────────────────────────────────────────
const MARKER_TYPES = [
  { type: 'safe',      labelKey: 'markerSafe',      icon: SecurityIcon,        color: '#22c55e' },
  { type: 'overnight', labelKey: 'markerOvernight',  icon: HotelIcon,           color: '#8b5cf6' },
  { type: 'poi',       labelKey: 'markerPoi',        icon: RoomIcon,            color: '#f59e0b' },
  { type: 'fuel',      labelKey: 'markerFuel',       icon: LocalGasStationIcon, color: '#ef4444' },
];
const MARKER_TO_LOCATION_TYPE = { safe: 'safe_point', overnight: 'overnight', poi: 'poi', fuel: 'fuel' };

// ─── Location type config (for nearby locations panel) ─────────────────────
const LOCATION_TYPE_CONFIG = {
  warehouse:  { icon: HomeIcon,            color: '#6366f1', labelKey: 'typeWarehouse' },
  safe_point: { icon: SecurityIcon,        color: '#22c55e', labelKey: 'markerSafe' },
  overnight:  { icon: HotelIcon,           color: '#8b5cf6', labelKey: 'markerOvernight' },
  poi:        { icon: RoomIcon,            color: '#f59e0b', labelKey: 'markerPoi' },
  fuel:       { icon: LocalGasStationIcon, color: '#ef4444', labelKey: 'markerFuel' },
  garage:     { icon: RoomIcon,            color: '#3b82f6', labelKey: 'typeGarage' },
  other:      { icon: RoomIcon,            color: '#64748b', labelKey: 'other' },
};

// ─── Geo helpers ────────────────────────────────────────────────────────────
function haversineKm([lng1, lat1], [lng2, lat2]) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function distanceAlongRoute(routeCoords, point) {
  if (!routeCoords || !point) return null;
  const [pLng, pLat] = point;
  let minDist = Infinity;
  let nearestIdx = 0;
  for (let i = 0; i < routeCoords.length; i++) {
    const [cLng, cLat] = routeCoords[i];
    const d = (cLng - pLng) ** 2 + (cLat - pLat) ** 2;
    if (d < minDist) { minDist = d; nearestIdx = i; }
  }
  let total = 0;
  const R = 6371;
  for (let i = 1; i <= nearestIdx; i++) {
    const [x1, y1] = routeCoords[i - 1];
    const [x2, y2] = routeCoords[i];
    const dLat = (y2 - y1) * Math.PI / 180;
    const dLng = (x2 - x1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(y1 * Math.PI / 180) * Math.cos(y2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  return Math.round(total);
}

// ─── MapToolbar ─────────────────────────────────────────────────────────────
export default function MapToolbar() {
  const dispatch = useDispatch();
  const tbTheme = usePlannerTheme();
  const tbDark = tbTheme === 'dark';
  // Paleta da barra flutuante do mapa (segue o tema do avatar). É renderizada
  // sobre o mapa (fora da raiz temada), por isso usa objeto JS, não tokens CSS.
  const tbp = tbDark
    ? { barBg: '#0e1b2b', barBorder: 'rgba(110,193,228,0.18)', barShadow: '0 4px 16px rgba(0,0,0,0.5)',
        sep: 'rgba(255,255,255,0.14)', chipActiveBg: 'rgba(255,255,255,0.10)', chipActiveText: '#eaf3fb', chipInactiveText: '#9fb3c8',
        btnBg: '#122133', btnText: '#eaf3fb', btnBorder: 'rgba(110,193,228,0.22)', btnHover: '#16263a', text: '#c4d3e3' }
    : { barBg: '#f1f5f9', barBorder: '#e2e8f0', barShadow: '0 1px 4px rgba(0,0,0,0.06)',
        sep: '#d1d5db', chipActiveBg: '#ffffff', chipActiveText: '#1e293b', chipInactiveText: '#64748b',
        btnBg: '#ffffff', btnText: '#1e293b', btnBorder: '#e2e8f0', btnHover: '#f8fafc', text: '#334155' };
  const route = useSelector(selectCurrentRoute);
  const actualTrail = useSelector(selectActualTrail);
  const { lang, sidebarVisible, sidebarCollapsed, previousView } = useSelector(selectUI);
  const { token, clientExternalId, clientSettings, userPermission } = useContext(AuthContext);
  // Fase B — viewer é só leitura: esconde gravar/despachar no planner.
  const canWrite = userPermission !== 'viewer';
  // Move Express só opera distribuição de combustível e NÃO usa app móvel
  // do motorista — comunicação é por telefone/operador. Esconder o botão
  // "Gravar e Despachar" para não enviar push à app que não existe.
  const hideDispatchForMoveExpress = isMoveExpressTenant(clientExternalId, clientSettings);
  const locations = useSelector(selectLocations);

  const [openPanel, setOpenPanel] = useState(null); // 'options' | 'nearby' | 'markers'
  const { mapClickMode } = useSelector(selectUI);
  const vehicles = useSelector(selectVehicles);
  const allDrivers = useSelector(selectDrivers);
  const saving = useSelector(selectSaving);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  // Dialog de confirmação do "Gravar e Despachar" — substitui o save
  // imediato por um popup com resumo da rota (motorista, veículo, OTs)
  // para o operador confirmar antes do despacho final.
  const [dispatchConfirmOpen, setDispatchConfirmOpen] = useState(false);
  // Reverse-geocode da origem para o popup de despacho — quando a partida
  // é "Posição actual do veículo" só temos lat/lng. Cache local por
  // coordenada para evitar refazer a chamada se o popup re-abrir.
  const [originResolvedAddress, setOriginResolvedAddress] = useState(null);
  const reverseGeocodeCacheRef = useRef({});
  const [dispatchInProgress, setDispatchInProgress] = useState(false);

  // Stock da viatura para o popup de despacho (Petrotec AO). Mostrado
  // como discriminação visível antes de confirmar — operador sabe o que
  // o técnico tem disponível para a intervenção.
  const [vehicleStock, setVehicleStock] = useState([]);
  const [vehicleStockLoading, setVehicleStockLoading] = useState(false);

  // Helper para extrair (lat, lng) de um waypoint do route — suporta os
  // dois formatos: { lat, lng } directos OU { coordinates: [lng, lat] }.
  function pickLatLng(o) {
    if (!o) return [null, null];
    if (o.lat != null && o.lng != null) return [Number(o.lat), Number(o.lng)];
    if (o.latitude != null && o.longitude != null) return [Number(o.latitude), Number(o.longitude)];
    if (Array.isArray(o.coordinates) && o.coordinates.length === 2) {
      return [Number(o.coordinates[1]), Number(o.coordinates[0])];
    }
    return [null, null];
  }

  // Quando o popup abre e a origem tem coords mas não tem morada (caso
  // típico: "Posição actual do veículo" — só lat/lng), pede um
  // reverse-geocode ao Mapbox. Sem isto o operador via apenas "🚗 Posição
  // actual do veículo" sem saber em que rua/cidade o técnico está.
  const [origLatForGeo, origLngForGeo] = pickLatLng(route?.origin);
  useEffect(() => {
    if (!dispatchConfirmOpen) { setOriginResolvedAddress(null); return; }
    const o = route?.origin;
    if (!o || o.address) return;
    if (origLatForGeo == null || origLngForGeo == null) return;
    const key = `${origLatForGeo.toFixed(5)},${origLngForGeo.toFixed(5)}`;
    const cached = reverseGeocodeCacheRef.current[key];
    if (cached !== undefined) { setOriginResolvedAddress(cached); return; }
    let cancelled = false;
    reverseGeocode(origLngForGeo, origLatForGeo)
      .then(addr => {
        if (cancelled) return;
        reverseGeocodeCacheRef.current[key] = addr || null;
        setOriginResolvedAddress(addr || null);
      })
      .catch(() => { if (!cancelled) setOriginResolvedAddress(null); });
    return () => { cancelled = true; };
  }, [dispatchConfirmOpen, origLatForGeo, origLngForGeo, route?.origin?.address]);

  // Carregar stock da viatura quando o popup abre. Endpoint já filtra
  // por quantity>0, portanto se o array vier vazio é mesmo "sem stock"
  // (ou viatura ainda sem registo local — backend devolve isShadow=true).
  const [vehicleStockShadow, setVehicleStockShadow] = useState(false);
  useEffect(() => {
    if (!dispatchConfirmOpen) { setVehicleStock([]); setVehicleStockShadow(false); return; }
    const vId = route?.vehicleId;
    if (!vId || !token) { setVehicleStock([]); setVehicleStockShadow(false); return; }
    let cancelled = false;
    setVehicleStockLoading(true);
    fetch(`/planner-api/vehicle-stock?vehicleId=${vId}`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        setVehicleStock(Array.isArray(data?.balances) ? data.balances : []);
        setVehicleStockShadow(!!data?.isShadow);
      })
      .catch(() => { if (!cancelled) { setVehicleStock([]); setVehicleStockShadow(false); } })
      .finally(() => { if (!cancelled) setVehicleStockLoading(false); });
    return () => { cancelled = true; };
  }, [dispatchConfirmOpen, route?.vehicleId, token]);

  // Aviso "geometria alterada mas não recalculada" antes de gravar/despachar.
  // Guarda a ação a executar se o operador optar por continuar mesmo assim.
  const [staleConfirm, setStaleConfirm] = useState(null);
  // Popup de aviso: ao gravar um re-plano em que o destino da rota mudou, a
  // morada de entrega da OT vai ser alterada (Opção A). Guarda { newName, proceed }.
  const [otDeliveryConfirm, setOtDeliveryConfirm] = useState(null);
  // Dialog de confirmação "Rota gravada" (Move Express). Mostra um resumo
  // pertinente (km, duração, motorista, OT) e botão para voltar a Ordens.
  const [savedConfirm, setSavedConfirm] = useState(null);

  // Draggable toolbar — centered by default, user can drag
  const [toolbarPos, setToolbarPos] = useState(null); // null = auto-centered (top: 68 below header)
  const [userDragged, setUserDragged] = useState(false);
  const dragRef = useRef(null);
  function onToolbarDragStart(e) {
    if (e.target.closest('svg') || e.target.tagName === 'path' || e.target.tagName === 'circle' || e.target.tagName === 'rect' || e.target.tagName === 'line' || e.target.tagName === 'ellipse') return;
    if (e.target.closest('[data-nodrag]')) return;
    const rect = dragRef.current?.getBoundingClientRect();
    const curX = toolbarPos ? toolbarPos.x : (rect ? rect.left : 0);
    const curY = toolbarPos ? toolbarPos.y : (rect ? rect.top : 0);
    const startX = e.clientX - curX;
    const startY = e.clientY - curY;
    function onMove(ev) { setUserDragged(true); setToolbarPos({ x: ev.clientX - startX, y: ev.clientY - startY }); }
    function onUp() { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  // Route options state
  const opts = route.routeOptions || {};
  const currency = opts.currency || 'EUR';
  const fuelType = route.fuelType || 'diesel';
  const defaultPrice = getSavedFuelPrice(currency, fuelType);
  const [priceInput, setPriceInput] = useState(String(route.fuelPrice ?? defaultPrice));
  const prevCurrencyRef = useRef(currency);
  const prevFuelTypeRef = useRef(fuelType);
  const [openCountries, setOpenCountries] = useState({});

  // Markers state
  const markers = route.routeMarkers || [];
  const [locPopover, setLocPopover] = useState(null);

  // Close panel when route is cleared
  const prevRouteRef = useRef(route.computedRoute);
  useEffect(() => {
    if (prevRouteRef.current && !route.computedRoute) {
      setOpenPanel(null);
    }
    prevRouteRef.current = route.computedRoute;
  }, [route.computedRoute]);

  // Nearby locations state
  const [maxDist, setMaxDist] = useState(30);

  // When currency or fuelType changes, load the saved price
  useEffect(() => {
    const currencyChanged = prevCurrencyRef.current !== currency;
    const fuelChanged = prevFuelTypeRef.current !== fuelType;
    if (currencyChanged || fuelChanged) {
      const saved = getSavedFuelPrice(currency, fuelType);
      setPriceInput(String(saved));
      dispatch(setFuelPrice(saved));
      prevCurrencyRef.current = currency;
      prevFuelTypeRef.current = fuelType;
    }
  }, [currency, fuelType]); // eslint-disable-line

  // Load locations when nearby panel opens
  useEffect(() => {
    if (openPanel === 'nearby' && token && locations.length === 0) {
      dispatch(fetchLocations(token));
    }
  }, [openPanel, token, locations.length, dispatch]);

  function toggleOpt(key) {
    dispatch(setRouteOptions({ [key]: !opts[key] }));
  }

  function togglePanel(name) {
    setOpenPanel(p => p === name ? null : name);
  }

  // Nearby locations computation
  const routeCoords = route.computedRoute?.geometry?.coordinates;
  const nearbyLocations = React.useMemo(() => {
    if (!routeCoords || locations.length === 0) return [];
    const sampled = routeCoords.filter((_, i) => i % 10 === 0 || i === routeCoords.length - 1);
    return locations
      .map(loc => {
        const coord = [Number(loc.longitude), Number(loc.latitude)];
        let minD = Infinity;
        for (const rc of sampled) {
          const d = haversineKm(coord, rc);
          if (d < minD) minD = d;
        }
        return { ...loc, distToRoute: Math.round(minD * 10) / 10 };
      })
      .filter(loc => loc.distToRoute <= maxDist)
      .sort((a, b) => {
        const kmA = distanceAlongRoute(routeCoords, [Number(a.longitude), Number(a.latitude)]);
        const kmB = distanceAlongRoute(routeCoords, [Number(b.longitude), Number(b.latitude)]);
        return (kmA || 0) - (kmB || 0);
      });
  }, [routeCoords, locations, maxDist]);

  // Load locations when markers panel opens
  useEffect(() => {
    if (openPanel === 'markers' && token && locations.length === 0) {
      dispatch(fetchLocations(token));
    }
  }, [openPanel, token, locations.length, dispatch]);

  function handleMarkerBtnClick(e, type) {
    const locType = MARKER_TO_LOCATION_TYPE[type];
    const typeLocations = locations.filter(l => l.type === locType);
    if (typeLocations.length > 0) {
      setLocPopover({ anchorEl: e.currentTarget, markerType: type });
    } else {
      handleAddMarkerManual(type);
    }
  }

  function handleAddMarkerManual(type) {
    setLocPopover(null);
    const id = `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    dispatch(addRouteMarker({ id, type, name: '', coordinates: null, notes: '' }));
    dispatch(setMapClickMode(`marker-${id}`));
  }

  function handleUseSavedLocation(type, loc) {
    setLocPopover(null);
    const id = `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    dispatch(addRouteMarker({ id, type, name: loc.name, coordinates: [Number(loc.longitude), Number(loc.latitude)], notes: loc.notes || '' }));
  }

  function handleSaveMarkerAsLocation(marker) {
    if (!token || !marker.coordinates || !marker.name) return;
    dispatch(createLocation({
      token,
      location: {
        name: marker.name,
        type: MARKER_TO_LOCATION_TYPE[marker.type] || 'poi',
        latitude: marker.coordinates[1],
        longitude: marker.coordinates[0],
        address: marker.name,
      },
    }));
  }

  function handleAddAsWaypoint(loc) {
    dispatch(addWaypoint({ name: loc.name, coordinates: [Number(loc.longitude), Number(loc.latitude)] }));
    dispatch(setNeedsRecalculation(true));
  }

  // ── Button style ────────────────────────────────────────────────────────
  function btnStyle(active) {
    return {
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', cursor: 'pointer', userSelect: 'none',
      borderRadius: 6,
      background: active ? 'rgba(59,130,246,0.22)' : 'transparent',
      border: active ? '1px solid rgba(59,130,246,0.45)' : '1px solid transparent',
      color: active ? '#93c5fd' : 'rgba(255,255,255,0.82)',
      transition: 'all 0.15s',
    };
  }

  // ── Panel shared styles ─────────────────────────────────────────────────
  const panelStyle = {
    borderRadius: 12,
    padding: '12px 14px',
    background: '#ffffff',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  };

  const sectionLabel = {
    fontSize: '0.6rem', fontWeight: 700, color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 4,
  };

  // Route stats
  const cr = route.computedRoute;
  const hideToolbar = route.status === 'idle' && !cr;
  const distKm = cr ? (cr.distance / 1000).toFixed(1) : null;
  // Mercadoria/Casais: o tempo total da rota inclui os tempos de PARAGEM
  // (tempo real da entrega), não só a condução. Gated à variante mercadoria.
  const isMercadoria = clientSettings?.uiVariant === 'mercadoria';
  const stopsSecTotal = isMercadoria
    ? ((route.waypoints || []).reduce((s, wp) => s + (Number(wp.stopDuration) || 0), 0)
      + (route.stops || []).reduce((s, st) => s + (Number(st.duration) || 0), 0)) * 60
    : 0;
  const totalDurSec = cr ? (cr.duration + stopsSecTotal) : null;
  const durH = totalDurSec != null ? Math.floor(totalDurSec / 3600) : null;
  const durM = totalDurSec != null ? Math.floor((totalDurSec % 3600) / 60) : null;
  const durStr = cr ? `${durH}h ${durM}m` : null;
  const stopsCount = route.stops?.length || 0;

  const statusCfg = {
    idle:        { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8', border: 'rgba(148,163,184,0.25)' },
    calculating: { bg: 'rgba(245,158,11,0.18)',  color: '#f59e0b', border: 'rgba(245,158,11,0.35)' },
    ready:       { bg: 'rgba(34,197,94,0.18)',   color: '#4ade80', border: 'rgba(34,197,94,0.35)' },
    error:       { bg: 'rgba(239,68,68,0.18)',   color: '#f87171', border: 'rgba(239,68,68,0.35)' },
  };
  const sCfg = statusCfg[route.status] || statusCfg.idle;
  const statusLabel = route.status === 'calculating' ? t(lang, 'calculating')
    : route.status === 'ready' ? t(lang, 'routeReady')
    : route.status === 'error' ? t(lang, 'error')
    : t(lang, 'noRoute');
  const statusIcon = route.status === 'calculating'
    ? <HourglassEmptyIcon style={{ fontSize: 11, animation: 'spin 1.4s ease-in-out infinite', color: sCfg.color }} />
    : route.status === 'ready'
    ? <CheckCircleIcon style={{ fontSize: 11, color: sCfg.color }} />
    : route.status === 'error'
    ? <ErrorIcon style={{ fontSize: 11, color: sCfg.color }} />
    : <DirectionsIcon style={{ fontSize: 11, color: sCfg.color }} />;

  // dispatchToDriver:
  //   true  → status='planned', sentToVehicle=true, OTs linked, driver
  //           gets the PDF / push (legacy save-and-dispatch).
  //   false → status='planned' but `dispatch=false` server-side: route is
  //           saved but the driver is NOT notified yet (operator preparing
  //           future routes). OTs stay 'assigned' instead of 'in_transit'.
  function handleSaveRoute(name, dispatchToDriver = true) {
    if (!token) { console.error('[Save] No token!'); return; }
    const finalName = name || route.name;
    dispatch(setRouteName(finalName));
    const routeToSave = { ...route, name: finalName };
    // (Removidos `delete routeToSave.driverId/vehicleId` que estavam aqui —
    // estavam a zerar os campos antes do save, deixando rota sem motorista/
    // veículo persistidos. saveRouteToAPI propaga estes para o body do
    // POST/PUT e o backend resolve via ensureLocalVehicleId.)
    const linkedOT = route.cargoRef || route.name || 'OT';
    const hasSourceOT = !!(route.sourceOTId || (Array.isArray(route.sourceOTIds) && route.sourceOTIds.length > 0));
    console.log('[Save] Saving route:', finalName, 'dispatch:', dispatchToDriver);
    dispatch(saveRouteToAPI({ token, route: routeToSave, dispatch: dispatchToDriver }))
      .then((result) => {
        if (result.error) {
          console.error('[Save] Error:', result.error);
        } else {
          if (hasSourceOT && dispatchToDriver) {
            dispatch(addToast({
              type: 'success', severity: 'success',
              message: lang === 'pt'
                ? `📱 PDF da rota enviado ao motorista · ${linkedOT}`
                : `📱 Route PDF sent to driver · ${linkedOT}`,
            }));
          } else if (!dispatchToDriver) {
            dispatch(addToast({
              type: 'info', severity: 'info',
              message: lang === 'pt'
                ? '💾 Rota gravada — motorista NÃO foi notificado'
                : '💾 Route saved — driver NOT notified',
            }));
          }
          setTimeout(() => setShowSaveDialog(false), 2000);
        }
      })
      .catch(err => console.error('[Save] Failed:', err));
  }

  const { detailsPanelOpen } = useSelector(selectUI);

  // Check if a location is already in the route
  function isLocationInRoute(loc) {
    const coord = [Number(loc.longitude), Number(loc.latitude)];
    const threshold = 0.005;
    const pts = [route.origin, route.destination, ...(route.waypoints || [])].filter(Boolean);
    return pts.some(p => p.coordinates && Math.abs(p.coordinates[0] - coord[0]) < threshold && Math.abs(p.coordinates[1] - coord[1]) < threshold);
  }
  const sep = <Box style={{ width: 1, height: 22, background: tbp.sep, flexShrink: 0, opacity: 0.4 }} />;
  const chipStyle = (active) => ({
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
    fontSize: '0.78rem', fontWeight: 500, transition: 'all 0.15s',
    background: active ? tbp.chipActiveBg : 'transparent',
    color: active ? tbp.chipActiveText : tbp.chipInactiveText,
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
  });

  if (hideToolbar) return null;

  return (
    <Box style={toolbarPos
      ? { position: 'absolute', top: toolbarPos.y, left: toolbarPos.x, zIndex: 510, display: 'flex', flexDirection: 'column', gap: 6 }
      : (sidebarVisible && !sidebarCollapsed)
        ? { position: 'absolute', top: 68, left: '50%', transform: 'translateX(-50%)', zIndex: 510, display: 'flex', flexDirection: 'column', gap: 6, transition: 'all 0.3s ease' }
        : { position: 'absolute', top: 68, left: 16, zIndex: 510, display: 'flex', flexDirection: 'column', gap: 6, transition: 'all 0.3s ease' }
    }>

      {/* ── Unified toolbar ──────────────────────────────────────────── */}
      <Box
        ref={dragRef}
        onMouseDown={onToolbarDragStart}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 2,
          background: tbp.barBg,
          border: `1px solid ${tbp.barBorder}`,
          borderRadius: 12,
          padding: '4px 8px',
          boxShadow: tbp.barShadow,
          height: 42,
          cursor: 'grab',
          userSelect: 'none',
        }}>
        {/* Botão "Voltar a X" contextual — primeiro elemento da fila para
            ficar bem à esquerda, longe dos controlos de zoom do mapa que
            estão no canto superior direito. Aparece quando a rota é
            modo consulta E há um ecrã anterior conhecido em previousView.
            Label dinâmico: "Voltar a Ordens" / "Voltar a Rotas" / etc. */}
        {route.readOnly && previousView && previousView !== 'planner' && (() => {
          const viewLabels = {
            dashboard: lang === 'pt' ? 'Dashboard' : 'Dashboard',
            fleet: lang === 'pt' ? 'Frota' : 'Fleet',
            orders: lang === 'pt' ? 'Ordens' : 'Orders',
            routes: lang === 'pt' ? 'Rotas' : 'Routes',
            reports: lang === 'pt' ? 'Relatórios' : 'Reports',
            vrp: 'VRP',
            manage: lang === 'pt' ? 'Gerir' : 'Manage',
            favorites: lang === 'pt' ? 'Favoritos' : 'Favorites',
            planner: lang === 'pt' ? 'Planeamento' : 'Planner',
          };
          const label = viewLabels[previousView] || previousView;
          return (<>
            <Tooltip title={lang === 'pt' ? `Voltar a ${label}` : `Back to ${label}`}>
              <Box
                data-nodrag
                onClick={() => {
                  dispatch(setActiveView(previousView));
                  dispatch(clearRoute());
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px',
                  borderRadius: 6,
                  background: tbp.btnBg,
                  color: tbp.btnText,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  border: `1px solid ${tbp.btnBorder}`,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = tbp.btnHover; }}
                onMouseLeave={e => { e.currentTarget.style.background = tbp.btnBg; }}
              >
                <ArrowBackIcon style={{ fontSize: 13 }} />
                {lang === 'pt' ? `Voltar a ${label}` : `Back to ${label}`}
              </Box>
            </Tooltip>
            {sep}
          </>);
        })()}
        {/* Details toggle */}
        {cr && (<>
          <Tooltip title={lang === 'pt' ? 'Detalhes da Rota' : 'Route Details'}>
            <Box onClick={() => dispatch(toggleDetailsPanel())} style={{
              ...chipStyle(detailsPanelOpen),
              padding: '4px 8px',
              color: detailsPanelOpen ? '#3b82f6' : '#94a3b8',
            }}>
              <InfoOutlinedIcon style={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
          {sep}
        </>)}
        {/* Distance */}
        {distKm && (<>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0 8px', color: tbp.text, fontSize: '0.75rem', fontWeight: 500 }}>
            <span style={{ color: '#94a3b8', fontSize: '0.65rem' }}>{t(lang, 'distance')}</span>
            {distKm} km
          </Box>
          {sep}
        </>)}
        {/* Duration */}
        {durStr && (<>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0 8px', color: tbp.text, fontSize: '0.75rem', fontWeight: 500 }}>
            <span style={{ color: '#94a3b8', fontSize: '0.65rem' }}>{t(lang, 'duration')}</span>
            {durStr}
          </Box>
          {sep}
        </>)}
        {/* Stops — opens sidebar stops tab */}
        {stopsCount > 0 && route.routeOptions?.useTachograph !== false && (<>
          <Tooltip title={lang === 'pt' ? 'Ver paragens no painel' : 'View stops in panel'}>
            <Box onClick={() => dispatch(setSidebarTab(2))} style={{
              ...chipStyle(false), padding: '4px 8px',
            }}>
              <span style={{ color: '#94a3b8', fontSize: '0.65rem' }}>{t(lang, 'stops')}</span>
              <span style={{ color: '#f59e0b', fontSize: '0.68rem', fontWeight: 600 }}>{stopsCount}</span>
            </Box>
          </Tooltip>
          {sep}
        </>)}
        {/* Status / Consulta badge */}
        {route.readOnly ? (
          <Box style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px',
            borderRadius: 8, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.02em',
            background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
            color: '#4338ca', border: '1px solid #c7d2fe',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#4338ca"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
            {lang === 'pt' ? 'Consulta' : 'View only'}
          </Box>
        ) : (
          null
        )}
        {/* Replay do percurso real — SÓ para rotas CONCLUÍDAS com percurso
            registado. Em curso, o operador usa o Live (não é substituído). */}
        {actualTrail?.trail?.length > 1 && route.dbStatus === 'completed' && (<>
          {sep}
          <Tooltip title={lang === 'pt' ? 'Reproduzir o percurso real do veículo' : 'Replay the vehicle actual route'} arrow>
            <Box onClick={() => window.dispatchEvent(new CustomEvent('openRouteReplay'))}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none',
                padding: '4px 12px', borderRadius: 8, fontSize: '0.68rem', fontWeight: 800,
                background: 'linear-gradient(135deg, #fb923c, #f97316)', color: '#fff',
                boxShadow: '0 2px 8px rgba(249,115,22,0.4)',
              }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
              {lang === 'pt' ? 'Replay do percurso' : 'Replay route'}
            </Box>
          </Tooltip>
        </>)}
        {/* Botão "Fechar rota" — colado à toolbar flutuante (acompanha
            quando arrastada). Aparece para inspect-mode da Path B
            (Rotas Gravadas no Planeamento). Reproduz exactamente o
            comportamento do "Limpar Rota" do painel esquerdo + fecha
            o sidebar para a search bar do mapa reaparecer. */}
        {typeof window !== 'undefined' && window.__inspectingRoute && !window.__fromOTScreen && (
          <Box
            data-nodrag
            onClick={() => {
              window.__inspectingRoute = false;
              window.__monitoringOT = null;
              window.__monitoringRouteLegs = null;
              window.dispatchEvent(new CustomEvent('highlightOTLeg', { detail: { orderId: null, routeLegs: [] } }));
              window.dispatchEvent(new CustomEvent('focusSingleVehicle', { detail: { id: null } }));
              dispatch(clearRoute());
              dispatch(setShowDetails(false));
              dispatch(setDisplayedSavedRoutes([]));
              dispatch(setResetMapView(true));
              dispatch(setSidebarVisible(false));
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
              fontSize: '0.7rem', fontWeight: 700, color: '#fff',
              background: 'linear-gradient(135deg, #475569, #334155)',
              border: '1px solid #334155',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #334155, #1e293b)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #475569, #334155)'; }}
            title={lang === 'pt' ? 'Fechar rota e voltar à pesquisa' : 'Close route and back to search'}
          >
            <CloseIcon style={{ fontSize: 13 }} />
            {lang === 'pt' ? 'Fechar rota' : 'Close route'}
          </Box>
        )}
        {/* renderiza o continue antigo do bloco para manter estrutura */}
        {!route.readOnly && (
          <Box style={{
            display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px',
            borderRadius: 6, fontSize: '0.60rem', fontWeight: 500, letterSpacing: '0.02em',
            background: sCfg.bg, color: sCfg.color,
            animation: route.status === 'calculating' ? 'pulse 1.4s ease-in-out infinite' : 'none',
          }}>
            {statusIcon}
            {statusLabel}
          </Box>
        )}
        {/* Badge do provider movido para o painel esquerdo (RouteForm)
            por pedido do utilizador — local com mais espaço para mostrar
            tooltip completa com explicação do provider usado. */}
        {/* ─────────── Toolbar de acção unificada ─────────────────
            Substitui (a) o botão Save antigo deste sítio, (b) a barra
            flutuante "Guardar e Despachar" do AppLayout, e (c) parte
            da SaveRouteDialog modal.
            Modo é decidido por getToolbarMode() abaixo do componente.
            Os botões aparecem inline com o resto da info da rota
            (distância, duração, status) — uma só fila visual.
            ─────────────────────────────────────────────────────────── */}
        {(() => {
          if (route.status !== 'ready') return null;
          if (route.readOnly) return null;
          // Detectar modo da rota
          const hasSourceOT = !!(route.sourceOTId
            || (Array.isArray(route.sourceOTIds) && route.sourceOTIds.length > 0));
          const hasVehicleAndDriver = !!(route.vehicleId && route.driverId);

          // Modo MANUAL (criação livre): mantém Save button antigo que
          // abre o modal SaveRouteDialog (operador escolhe o nome).
          if (!hasSourceOT && !route.dbId) {
            if (!canWrite) return null; // viewer: sem gravar
            return (<>
              {sep}
              <Tooltip title={t(lang, 'headerSaveRoute')}>
                <Box onClick={() => setShowSaveDialog(true)} style={{
                  ...chipStyle(false),
                  padding: '4px 10px',
                  background: '#ffffff', color: '#3b82f6',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#3b82f6'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#3b82f6'; }}
                >
                  <SaveIcon style={{ fontSize: 13 }} />
                  {t(lang, 'save')}
                </Box>
              </Tooltip>
            </>);
          }

          // Modos OT-derivada: acções INLINE (sem modal). Como a rota
          // já tem nome auto-gerado, não precisamos do diálogo para
          // pedir input — guardamos directamente.
          // Replanning mode: chega aqui via "Editar rota no mapa" da lib
          // de rotas. dbId já existe e replanningRouteId está set. Save
          // precisa de re-dispatch (POST /routes/dispatch) e, se a rota
          // estava in_progress, restaurar esse status (saveRouteToAPI
          // promove para planned por defeito).
          const isReplanning = !!route.replanningRouteId;
          const wasLive = route.dbStatus === 'in_progress';
          // Voltar atrás respeitando o contexto:
          //  · previousView (uiSlice) — fonte canónica de "de onde vim"
          //  · flags window legacy — fallback para fluxos que ainda não migraram
          //  · default 'orders' — Operações é o ponto de partida natural
          const returnView = previousView && previousView !== 'planner'
            ? previousView
            : (window.__fromRoutesLibrary
                ? 'routes'
                : (window.__fromOTScreen ? 'orders' : 'orders'));

          const finishAndExit = () => {
            if (token) dispatch(fetchSavedRoutes(token));
            dispatch(setReplanningRouteId(null));
            dispatch(clearRoute());
            dispatch(navigateTo(returnView));
            window.__fromRoutesLibrary = false;
            window.__fromOTScreen = false;
            window.__inspectingRoute = false;
          };

          const handleCancel = () => {
            // Descarta edições e volta ao ecrã anterior. Se a rota nunca
            // foi gravada (sem dbId), os waypoints/origem/destino em
            // Redux ficam descartados. OTs não são desassociadas na BD
            // porque ainda não foram associadas — só são no save.
            dispatch(setReplanningRouteId(null));
            dispatch(clearRoute());
            dispatch(navigateTo(returnView));
            window.__fromRoutesLibrary = false;
            window.__fromOTScreen = false;
            window.__inspectingRoute = false;
          };

          const reDispatchAndRestoreStatus = async (routeId) => {
            try {
              const drvRes = await fetch(`/planner-api/routes/${routeId}/drivers`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (drvRes.ok) {
                const rds = await drvRes.json();
                const assignments = (rds || [])
                  .filter(rd => rd.driverId)
                  .map(rd => ({ routeId, driverId: rd.driverId }));
                if (assignments.length > 0) {
                  await fetch('/planner-api/routes/dispatch', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ assignments }),
                  });
                }
              }
              // Restaurar in_progress só quando a rota já estava em curso.
              // saveRouteToAPI promove para 'planned' por defeito, o que
              // estaria errado para uma rota que o motorista já iniciou.
              if (wasLive) {
                await fetch(`/planner-api/routes/${routeId}/status`, {
                  method: 'PATCH',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ status: 'in_progress' }),
                });
              }
            } catch (e) {
              console.error('[Replan] Re-dispatch failed:', e);
            }
          };

          // ── Destino da rota → morada de entrega da OT (Opção A) ──────────
          // Ao mudar o destino no re-plano, a entrega da OT fica desalinhada.
          // Antes de gravar, avisamos e (se o operador confirmar) sincronizamos
          // a entrega da OT = destino final da rota. O destino antigo fica como
          // paragem (não se remove). Só OT única; fuel tem fluxo próprio.
          const syncOTId = (!Array.isArray(route.sourceOTIds) || route.sourceOTIds.length <= 1)
            ? (route.sourceOTId || route.destination?.otId || (Array.isArray(route.sourceOTIds) ? route.sourceOTIds[0] : null) || null)
            : null;
          const checkOTDeliveryChanged = async () => {
            // Move Express / variante fuel: fluxo próprio — NUNCA toca aqui.
            if (hideDispatchForMoveExpress) return null;
            const dest = route.destination;
            if (!syncOTId || !Array.isArray(dest?.coordinates)) return null;
            try {
              const res = await fetch(`/planner-api/transport-orders/${syncOTId}`, { headers: { Authorization: `Bearer ${token}` } });
              if (!res.ok) return null;
              const j = await res.json();
              const ot = j?.data || j;
              if (!ot || ot.serviceType === 'fuel') return null;
              const [lng, lat] = dest.coordinates;
              const coordsChanged = Math.abs(Number(ot.deliveryLat) - Number(lat)) > 1e-5 || Math.abs(Number(ot.deliveryLng) - Number(lng)) > 1e-5;
              const nameChanged = String(ot.deliveryName || '') !== String(dest.name || '');
              return (coordsChanged || nameChanged) ? { newName: dest.name || '' } : null;
            } catch { return null; }
          };
          const applyOTDeliverySync = async () => {
            const dest = route.destination;
            if (!syncOTId || !Array.isArray(dest?.coordinates)) return;
            const [lng, lat] = dest.coordinates;
            try {
              await fetch(`/planner-api/transport-orders/${syncOTId}`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ deliveryName: dest.name || null, deliveryAddress: dest.address || dest.name || null, deliveryLat: Number(lat), deliveryLng: Number(lng) }),
              });
            } catch (e) { console.error('[OT delivery sync] error:', e); }
          };

          const handleSaveOnly = async (force = false, otConfirmed = false) => {
            if (!token) return;
            // Geometria alterada mas não recalculada → avisa antes de gravar.
            if (!force && route.computedRoute && route.status !== 'ready') {
              setStaleConfirm({ proceed: () => handleSaveOnly(true, otConfirmed) });
              return;
            }
            // Destino mudou vs a entrega da OT? Avisa antes de gravar (Opção A).
            if (!otConfirmed) {
              const chg = await checkOTDeliveryChanged();
              if (chg) { setOtDeliveryConfirm({ newName: chg.newName, proceed: () => handleSaveOnly(force, true) }); return; }
            }
            const finalName = route.name || 'Rota sem nome';
            dispatch(setRouteName(finalName));
            const routeToSave = { ...route, name: finalName };
            // (Não apagamos driverId/vehicleId — bug histórico que deixava
            // a rota sem motorista/veículo persistidos.)
            const result = await dispatch(saveRouteToAPI({ token, route: routeToSave, dispatch: false }));
            if (result.error) {
              console.error('[Save] Error:', result.error);
              dispatch(addToast({
                type: 'error', severity: 'error',
                message: (lang === 'pt' ? '❌ Não foi possível gravar a rota: ' : '❌ Could not save route: ')
                  + (result.error.message || (lang === 'pt' ? 'erro desconhecido' : 'unknown error')),
              }));
              return;
            }
            // Opção A: destino confirmado → sincroniza a morada de entrega da OT.
            if (otConfirmed) await applyOTDeliverySync();
            if (isReplanning) {
              dispatch(addToast({
                type: 'success', severity: 'success',
                message: lang === 'pt'
                  ? '✅ Rota guardada (motorista mantém versão actual)'
                  : '✅ Route saved (driver keeps current version)',
              }));
              finishAndExit();
            } else if (hideDispatchForMoveExpress) {
              // Move Express: dialog de confirmação com resumo + botão
              // "Voltar a Ordens" (motorista não tem app, foi telefonema).
              const driverName = allDrivers?.find(d => Number(d.id) === Number(route.driverId))?.name || null;
              const vehiclePlate = vehicles?.find(v => v.id === route.vehicleId)?.plate || null;
              const otRef = (route.waypoints || []).map(w => w.orderNumber).filter(Boolean)[0]
                || route.destination?.orderNumber
                || null;
              setSavedConfirm({
                km: route.computedRoute?.totalDistance != null
                  ? (route.computedRoute.totalDistance / 1000).toFixed(1)
                  : null,
                mins: route.computedRoute?.totalDuration != null
                  ? Math.round(route.computedRoute.totalDuration / 60)
                  : null,
                driverName,
                vehiclePlate,
                cisterns: Array.isArray(route.cisterns) ? route.cisterns : [],
                otRef,
                origin: route.origin?.name || null,
                destination: route.destination?.name || null,
                routeId: result.payload?.id || result.payload?.dbId || null,
              });
            } else {
              dispatch(addToast({
                type: 'info', severity: 'info',
                message: lang === 'pt'
                  ? '💾 Rota gravada — motorista NÃO foi notificado'
                  : '💾 Route saved — driver NOT notified',
              }));
            }
          };

          const handleSaveDispatch = async (force = false, otConfirmed = false) => {
            if (!token) return;
            // Geometria alterada mas não recalculada → avisa antes de despachar.
            // (Em rotas in_progress o "+ Ordem" já re-otimiza sozinho, por isso
            // o status fica 'ready' e este aviso não dispara nesse caso.)
            if (!force && route.computedRoute && route.status !== 'ready') {
              setStaleConfirm({ proceed: () => handleSaveDispatch(true, otConfirmed) });
              return;
            }
            if (!isReplanning) {
              // Fluxo OT-derivada normal: abre dialog de confirmação
              // antes de despachar. A execução real (save + clear +
              // navigate) acontece em performDispatch quando o operador
              // confirma. Sem este popup, o despacho era imediato e
              // irreversível ao primeiro clique.
              setDispatchConfirmOpen(true);
              return;
            }
            // Replanning: destino mudou vs entrega da OT? Avisa antes (Opção A).
            if (!otConfirmed) {
              const chg = await checkOTDeliveryChanged();
              if (chg) { setOtDeliveryConfirm({ newName: chg.newName, proceed: () => handleSaveDispatch(force, true) }); return; }
            }
            // Replanning: PUT + re-dispatch + (opcional) PATCH status.
            const finalName = route.name || 'Rota sem nome';
            dispatch(setRouteName(finalName));
            const routeToSave = { ...route, name: finalName };
            // (Não apagamos driverId/vehicleId — bug histórico que deixava
            // a rota sem motorista/veículo persistidos.)
            const result = await dispatch(saveRouteToAPI({ token, route: routeToSave, dispatch: true }));
            if (result.error) {
              console.error('[Save] Error:', result.error);
              dispatch(addToast({
                type: 'error', severity: 'error',
                message: (lang === 'pt' ? '❌ Não foi possível gravar/despachar a rota: ' : '❌ Could not save/dispatch route: ')
                  + (result.error.message || (lang === 'pt' ? 'erro desconhecido' : 'unknown error')),
              }));
              return;
            }
            // Opção A: destino confirmado → sincroniza a morada de entrega da OT.
            if (otConfirmed) await applyOTDeliverySync();
            const savedId = result.payload?.id || route.replanningRouteId || route.dbId;
            if (savedId) await reDispatchAndRestoreStatus(savedId);
            dispatch(addToast({
              type: 'success', severity: 'success',
              message: wasLive
                ? (lang === 'pt' ? '✅ Rota atualizada e reenviada ao motorista' : '✅ Route updated and resent to driver')
                : (lang === 'pt' ? '✅ Rota planeada actualizada e reenviada' : '✅ Planned route updated and resent'),
            }));
            finishAndExit();
          };

          return (<>
            {sep}
            {/* Cancelar */}
            <Tooltip title={lang === 'pt' ? 'Descartar edições e voltar' : 'Discard changes and go back'}>
              <Box onClick={handleCancel} style={{
                ...chipStyle(false),
                padding: '4px 10px',
                background: '#f8fafc', color: '#64748b',
                border: '1px solid #e2e8f0',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fecaca'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
              >
                <CloseIcon style={{ fontSize: 13 }} />
                {lang === 'pt' ? 'Cancelar' : 'Cancel'}
              </Box>
            </Tooltip>
            {/* Só Gravar (sempre disponível para OT-derivadas) */}
            {canWrite && (
            <Tooltip title={hasSourceOT
              ? (lang === 'pt' ? 'Grava e associa a rota à OT — o motorista não é notificado' : 'Save and link the route to the OT — driver is not notified')
              : (lang === 'pt'
                ? (route.dbId ? 'Actualizar a rota sem notificar o motorista' : 'Guardar como rascunho — motorista não é notificado')
                : (route.dbId ? 'Update route without notifying driver' : 'Save as draft — driver is not notified'))}>
              <Box onClick={saving ? undefined : () => handleSaveOnly()} style={{
                ...chipStyle(false),
                padding: '4px 10px',
                background: saving ? '#cbd5e1' : '#ffffff',
                color: saving ? '#fff' : '#3b82f6',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                opacity: saving ? 0.6 : 1,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
                onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = '#3b82f6'; e.currentTarget.style.color = '#fff'; } }}
                onMouseLeave={e => { if (!saving) { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#3b82f6'; } }}
              >
                {saving ? <HourglassEmptyIcon style={{ fontSize: 13, animation: 'spin 1.4s ease-in-out infinite' }} /> : <SaveIcon style={{ fontSize: 13 }} />}
                {hasSourceOT
                  ? (lang === 'pt' ? 'Gravar rota da OT' : 'Save OT route')
                  : (lang === 'pt' ? 'Só Gravar' : 'Save only')}
              </Box>
            </Tooltip>
            )}
            {/* Gravar e Despachar / Reenviar — só com veículo + motorista
                atribuídos. Escondido para Move Express (não usa app móvel). */}
            {canWrite && hasVehicleAndDriver && !hideDispatchForMoveExpress && (
              <Tooltip title={lang === 'pt'
                ? (isReplanning ? 'Gravar e reenviar a nova versão ao motorista' : 'Gravar e enviar a rota ao motorista (push + app)')
                : (isReplanning ? 'Save and resend the new version to the driver' : 'Save and dispatch route to driver (push + app)')}>
                <Box onClick={saving ? undefined : () => handleSaveDispatch()} style={{
                  ...chipStyle(false),
                  padding: '4px 10px',
                  background: saving ? '#cbd5e1' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                  color: '#fff',
                  boxShadow: '0 2px 6px rgba(245,158,11,0.35)',
                  opacity: saving ? 0.6 : 1,
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
                  onMouseEnter={e => { if (!saving) e.currentTarget.style.background = 'linear-gradient(135deg, #d97706, #b45309)'; }}
                  onMouseLeave={e => { if (!saving) e.currentTarget.style.background = 'linear-gradient(135deg, #f59e0b, #d97706)'; }}
                >
                  {saving ? <HourglassEmptyIcon style={{ fontSize: 13, animation: 'spin 1.4s ease-in-out infinite' }} /> : <SendIcon style={{ fontSize: 13 }} />}
                  {lang === 'pt'
                    ? (isReplanning ? 'Gravar e Reenviar' : 'Gravar e Despachar')
                    : (isReplanning ? 'Save and Resend' : 'Save and Dispatch')}
                </Box>
              </Tooltip>
            )}
          </>);
        })()}
      </Box>

      {ReactDOM.createPortal(
        <SaveRouteDialog open={showSaveDialog} onClose={() => setShowSaveDialog(false)} route={route} vehicles={vehicles} drivers={allDrivers} lang={lang} onSave={handleSaveRoute} saving={saving} />,
        document.body
      )}

      {/* Dialog "Rota gravada com sucesso" — Move Express. Mostra resumo
          pertinente (origem→destino, km, motorista, OT) + botão para voltar
          ao ecrã de Ordens. Substitui o toast simples para tenants sem app
          móvel onde o operador precisa de feedback claro pós-gravação. */}
      {ReactDOM.createPortal(
        <Dialog open={!!savedConfirm} onClose={() => setSavedConfirm(null)} maxWidth="sm" fullWidth
          PaperProps={{ style: { borderRadius: 14, overflow: 'hidden' } }}>
          {savedConfirm && (
            <>
              <Box style={{
                background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 60%)',
                borderBottom: '1px solid #bbf7d0',
                padding: '18px 22px',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <Box style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: '#16a34a', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CheckCircleIcon style={{ fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography style={{ fontSize: '1.02rem', fontWeight: 800, color: '#15803d', letterSpacing: '-0.01em' }}>
                    {lang === 'pt' ? 'Rota gravada com sucesso' : 'Route saved successfully'}
                  </Typography>
                  <Typography style={{ fontSize: '0.66rem', fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>
                    {lang === 'pt' ? 'Move Express · Distribuição' : 'Move Express · Distribution'}
                  </Typography>
                </Box>
              </Box>
              <Box style={{ padding: '18px 22px', background: '#fafafa' }}>
                <Box style={{
                  background: '#fff', border: '1px solid #e2e8f0',
                  borderRadius: 10, padding: '12px 14px',
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                  {savedConfirm.origin && (
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Box style={{ width: 22, height: 22, borderRadius: 4, background: '#dcfce7', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>A</Box>
                      <Box style={{ flex: 1 }}>
                        <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{lang === 'pt' ? 'Origem' : 'Origin'}</Typography>
                        <Typography style={{ fontSize: '0.84rem', fontWeight: 600, color: '#0f172a' }}>{savedConfirm.origin}</Typography>
                      </Box>
                    </Box>
                  )}
                  {savedConfirm.destination && (
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Box style={{ width: 22, height: 22, borderRadius: 4, background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>B</Box>
                      <Box style={{ flex: 1 }}>
                        <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{lang === 'pt' ? 'Destino' : 'Destination'}</Typography>
                        <Typography style={{ fontSize: '0.84rem', fontWeight: 600, color: '#0f172a' }}>{savedConfirm.destination}</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
                <Box style={{
                  marginTop: 12,
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
                }}>
                  {(savedConfirm.km != null) && (
                    <Box style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                      <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{lang === 'pt' ? 'Distância' : 'Distance'}</Typography>
                      <Typography style={{ fontSize: '1.02rem', fontWeight: 800, color: '#0f172a', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                        {savedConfirm.km} km
                      </Typography>
                    </Box>
                  )}
                  {(savedConfirm.mins != null) && (
                    <Box style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                      <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{lang === 'pt' ? 'Duração' : 'Duration'}</Typography>
                      <Typography style={{ fontSize: '1.02rem', fontWeight: 800, color: '#0f172a', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                        {savedConfirm.mins >= 60 ? `${Math.floor(savedConfirm.mins / 60)}h ${savedConfirm.mins % 60}m` : `${savedConfirm.mins} min`}
                      </Typography>
                    </Box>
                  )}
                  {savedConfirm.driverName && (
                    <Box style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                      <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{lang === 'pt' ? 'Motorista' : 'Driver'}</Typography>
                      <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                        {savedConfirm.driverName}
                      </Typography>
                    </Box>
                  )}
                  {savedConfirm.vehiclePlate && (
                    <Box style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                      <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{lang === 'pt' ? 'Tractor' : 'Tractor'}</Typography>
                      <Typography style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                        {savedConfirm.vehiclePlate}
                      </Typography>
                    </Box>
                  )}
                  {Array.isArray(savedConfirm.cisterns) && savedConfirm.cisterns.length > 0 && (() => {
                    const total = savedConfirm.cisterns.reduce((s, c) => s + (Number(c.capacityM3) || 0), 0);
                    return (
                      <Box style={{ gridColumn: '1 / -1', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '10px 12px' }}>
                        <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {savedConfirm.cisterns.length > 1
                            ? (lang === 'pt' ? 'Cisternas atreladas' : 'Attached cisterns')
                            : (lang === 'pt' ? 'Cisterna atrelada' : 'Attached cistern')}
                        </Typography>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 3 }}>
                          {savedConfirm.cisterns.map((c, i) => (
                            <span key={i} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '2px 9px', borderRadius: 999,
                              background: '#fff', border: '1px solid #bbf7d0',
                              fontSize: '0.8rem', fontWeight: 800, color: '#0f172a',
                              fontFamily: 'ui-monospace, Menlo, monospace',
                            }}>
                              🛢 {c.plate}{c.capacityM3 != null && <span style={{ color: '#15803d', fontSize: '0.66rem' }}>{c.capacityM3} m³</span>}
                            </span>
                          ))}
                          {savedConfirm.cisterns.length > 1 && (
                            <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#15803d', fontFamily: 'ui-monospace, Menlo, monospace' }}>Σ {total} m³</span>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
                <Box style={{
                  marginTop: 12,
                  padding: '10px 14px',
                  background: '#fef3c7', border: '1px solid #fcd34d',
                  borderRadius: 8,
                  fontSize: '0.72rem', color: '#92400e',
                  lineHeight: 1.45,
                }}>
                  {lang === 'pt' ? (
                    <>
                      <strong>Próximo passo:</strong> avisa o motorista por telefone/WhatsApp.
                      Quando ele chegar ao terminal e carregar, abre a OT e clica em <strong>"Carregou"</strong> para registar a Ordem de Carregamento + revelar o PA real.
                    </>
                  ) : (
                    <>
                      <strong>Next step:</strong> notify the driver by phone/WhatsApp.
                      When they arrive at the terminal and load, open the OT and click <strong>"Loaded"</strong> to register the loading order and reveal the actual delivery point.
                    </>
                  )}
                </Box>
              </Box>
              <Box style={{ padding: '12px 22px', background: '#fff', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button onClick={() => setSavedConfirm(null)} style={{ textTransform: 'none', color: '#64748b' }}>
                  {lang === 'pt' ? 'Ficar no mapa' : 'Stay on map'}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setSavedConfirm(null);
                    // Replica finishAndExit (não temos acesso ao do IIFE).
                    if (token) dispatch(fetchSavedRoutes(token));
                    dispatch(setReplanningRouteId(null));
                    dispatch(clearRoute());
                    dispatch(navigateTo('orders'));
                    window.__fromRoutesLibrary = false;
                    window.__fromOTScreen = false;
                    window.__inspectingRoute = false;
                  }}
                  style={{
                    textTransform: 'none', fontWeight: 700,
                    background: 'linear-gradient(135deg, #16a34a, #15803d)',
                    color: '#fff',
                    boxShadow: '0 2px 6px rgba(22,163,74,0.25)',
                  }}
                >
                  {lang === 'pt' ? 'Voltar a Ordens →' : 'Back to Orders →'}
                </Button>
              </Box>
            </>
          )}
        </Dialog>,
        document.body
      )}

      {/* Aviso antes de gravar/despachar quando a geometria foi alterada mas
          não recalculada — evita enviar ao motorista o trajeto antigo (não
          otimizado, sem a nova paragem no percurso). */}
      <ConfirmDialog
        open={!!staleConfirm}
        severity="warning"
        title={lang === 'pt' ? 'Rota alterada mas não recalculada' : 'Route changed but not recalculated'}
        message={lang === 'pt'
          ? 'Alteraste paragens e ainda não recalculaste a rota. Se continuares, o motorista recebe o trajeto ANTIGO — a nova paragem não fica otimizada no percurso. Recomendado: Cancelar e clicar em ↻ Recalcular primeiro.'
          : 'You changed stops without recalculating. If you continue, the driver gets the OLD path — the new stop is not optimised. Recommended: Cancel and click ↻ Recalculate first.'}
        confirmLabel={lang === 'pt' ? 'Continuar assim mesmo' : 'Continue anyway'}
        cancelLabel={lang === 'pt' ? 'Cancelar' : 'Cancel'}
        onConfirm={() => { const p = staleConfirm && staleConfirm.proceed; setStaleConfirm(null); if (p) p(); }}
        onClose={() => setStaleConfirm(null)}
      />

      {/* Opção A — aviso de que a morada de ENTREGA da OT vai ser alterada
          para o novo destino da rota (o destino antigo fica como paragem). */}
      <ConfirmDialog
        open={!!otDeliveryConfirm}
        severity="warning"
        title={lang === 'pt' ? 'Alterar a morada de entrega da OT?' : 'Change the OT delivery address?'}
        message={lang === 'pt'
          ? `Mudaste o destino final da rota. Ao gravar, a MORADA DE ENTREGA da OT passa a ser "${otDeliveryConfirm?.newName || ''}". O destino anterior fica como paragem na rota. Continuar?`
          : `You changed the route's final destination. On save, the OT DELIVERY address becomes "${otDeliveryConfirm?.newName || ''}". The previous destination stays as a stop. Continue?`}
        confirmLabel={lang === 'pt' ? 'Sim, alterar morada' : 'Yes, change address'}
        cancelLabel={lang === 'pt' ? 'Não' : 'No'}
        onConfirm={() => { const p = otDeliveryConfirm && otDeliveryConfirm.proceed; setOtDeliveryConfirm(null); if (p) p(); }}
        onClose={() => setOtDeliveryConfirm(null)}
      />

      {/* Dialog de confirmação do despacho — abre quando o operador
          clica "Gravar e Despachar" no fluxo OT-derivado novo. Mostra
          resumo da rota, motorista e lista de OTs. Operador confirma →
          executa save + clearRoute + navigateTo('orders'). */}
      {ReactDOM.createPortal(
        <Dialog
          open={dispatchConfirmOpen}
          onClose={() => !dispatchInProgress && setDispatchConfirmOpen(false)}
          maxWidth={false}
          PaperProps={{ style: { width: 880, maxWidth: '96vw', maxHeight: '92vh', borderRadius: 16, overflow: 'hidden' } }}
        >
          {/* Header compacto */}
          <Box style={{
            padding: '12px 18px',
            background: 'linear-gradient(135deg, #fff7ed, #fed7aa)',
            borderBottom: '1px solid #fdba74',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <Box style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 3px 10px rgba(245,158,11,0.4)',
            }}>
              <SendIcon style={{ fontSize: 18 }} />
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography style={{ fontSize: '0.92rem', fontWeight: 800, color: '#7c2d12', lineHeight: 1.2 }}>
                {lang === 'pt' ? 'Confirmar despacho ao motorista' : 'Confirm dispatch to driver'}
              </Typography>
              <Typography style={{ fontSize: '0.7rem', color: '#9a3412' }}>
                {lang === 'pt' ? 'Confirma os detalhes antes de enviar a rota ao terminal do motorista.' : 'Confirm details before sending the route.'}
              </Typography>
            </Box>
            {/* KPIs grandes no header — visíveis sem scroll */}
            <Box style={{ display: 'flex', gap: 6 }}>
              {distKm && (
                <Box style={{ padding: '6px 10px', background: '#fff', borderRadius: 8, border: '1px solid #fed7aa', textAlign: 'center', minWidth: 56 }}>
                  <Typography style={{ fontSize: '0.55rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.05em' }}>KM</Typography>
                  <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{distKm}</Typography>
                </Box>
              )}
              {durStr && (
                <Box style={{ padding: '6px 10px', background: '#fff', borderRadius: 8, border: '1px solid #fed7aa', textAlign: 'center', minWidth: 56 }}>
                  <Typography style={{ fontSize: '0.55rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Duração</Typography>
                  <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{durStr}</Typography>
                </Box>
              )}
              {((route.waypoints || []).length + (route.destination ? 1 : 0)) > 0 && (
                <Box style={{ padding: '6px 10px', background: '#fff', borderRadius: 8, border: '1px solid #fed7aa', textAlign: 'center', minWidth: 56 }}>
                  <Typography style={{ fontSize: '0.55rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stops</Typography>
                  <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{(route.waypoints || []).length + (route.destination ? 1 : 0)}</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Body: layout em grelha 2 colunas */}
          <Box style={{
            padding: '14px 18px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 14,
            overflowY: 'auto',
            maxHeight: 'calc(92vh - 130px)',
          }}>
            {/* COLUNA ESQUERDA */}
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
              {/* Motorista + veículo */}
              <Box style={{ padding: '10px 12px', background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                  {lang === 'pt' ? 'Motorista & Veículo' : 'Driver & Vehicle'}
                </Typography>
                <Box style={{ display: 'flex', gap: 10 }}>
                  {/* Motorista */}
                  <Box style={{ flex: 1, minWidth: 0, padding: '8px 10px', background: '#fff', borderRadius: 8, border: '1px solid #dbeafe' }}>
                    <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', marginBottom: 2 }}>
                      {lang === 'pt' ? 'Motorista' : 'Driver'}
                    </Typography>
                    <Typography style={{ fontSize: '0.84rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                      {route.driverName || (allDrivers.find(d => Number(d.id) === Number(route.driverId))?.name) || `#${route.driverId || '—'}`}
                    </Typography>
                    {(() => {
                      const driverPhone = allDrivers.find(d => Number(d.id) === Number(route.driverId))?.phone;
                      if (!driverPhone) return null;
                      return (
                        <Typography component="a" href={`tel:${driverPhone}`}
                          style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1d4ed8', textDecoration: 'none', display: 'inline-block', marginTop: 2 }}>
                          📞 {driverPhone}
                        </Typography>
                      );
                    })()}
                  </Box>
                  {/* Veículo */}
                  <Box style={{ flex: 1, minWidth: 0, padding: '8px 10px', background: '#fff', borderRadius: 8, border: '1px solid #d1fae5' }}>
                    <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#10b981', textTransform: 'uppercase', marginBottom: 2 }}>
                      {lang === 'pt' ? 'Veículo' : 'Vehicle'}
                    </Typography>
                    <Typography style={{ fontSize: '0.84rem', fontWeight: 800, color: '#0f172a', fontFamily: 'ui-monospace, SFMono-Regular, monospace', lineHeight: 1.2 }}>
                      {route.vehiclePlate || (vehicles.find(v => Number(v.id) === Number(route.vehicleId))?.plate) || `#${route.vehicleId || '—'}`}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Rota: origem → destino com timeline visual */}
              <Box style={{ padding: '10px 12px', background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  {lang === 'pt' ? 'Itinerário' : 'Itinerary'}
                </Typography>
                <Box style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                  {/* Timeline vertical */}
                  <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
                    <Box style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', border: '2px solid #fff', boxShadow: '0 0 0 2px #22c55e' }} />
                    <Box style={{ flex: 1, width: 2, background: 'repeating-linear-gradient(180deg, #cbd5e1 0 4px, transparent 4px 8px)', minHeight: 24 }} />
                    <Box style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', border: '2px solid #fff', boxShadow: '0 0 0 2px #ef4444' }} />
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    {/* Origem */}
                    <Box style={{ marginBottom: 10 }}>
                      <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#16a34a', textTransform: 'uppercase' }}>
                        {lang === 'pt' ? 'Origem' : 'Origin'}
                      </Typography>
                      <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.25 }}>
                        {route.origin?.name || '—'}
                      </Typography>
                      {(() => {
                        const addr = route.origin?.address || originResolvedAddress;
                        if (!addr || addr === route.origin?.name) return null;
                        return (
                          <Typography style={{ fontSize: '0.66rem', color: '#475569', marginTop: 2, lineHeight: 1.3 }}>
                            📍 {addr}
                          </Typography>
                        );
                      })()}
                    </Box>
                    {/* Destino */}
                    <Box>
                      <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase' }}>
                        {lang === 'pt' ? 'Destino' : 'Destination'}
                      </Typography>
                      <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.25 }}>
                        {route.destination?.name || '—'}
                      </Typography>
                      {route.destination?.address && route.destination.address !== route.destination.name && (
                        <Typography style={{ fontSize: '0.66rem', color: '#475569', marginTop: 2, lineHeight: 1.3 }}>
                          📍 {route.destination.address}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* COLUNA DIREITA */}
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
              {/* Lista de OTs */}
              {((route.waypoints || []).some(w => w?.otId) || route.destination?.otId || route.sourceOTIds?.length > 0) && (() => {
                const seen = new Set();
                const otStops = [];
                (route.waypoints || []).forEach((w, idx) => {
                  if (w?.otId && !seen.has(w.otId)) {
                    seen.add(w.otId);
                    otStops.push({ order: idx + 1, ot: w, kind: 'waypoint' });
                  }
                });
                if (route.destination?.otId && !seen.has(route.destination.otId)) {
                  seen.add(route.destination.otId);
                  otStops.push({ order: otStops.length + 1, ot: route.destination, kind: 'destination' });
                }
                if (otStops.length === 0) return null;
                return (
                  <Box style={{ padding: '10px 12px', background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                    <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                      {lang === 'pt' ? 'Ordens incluídas' : 'Included orders'}
                      <span style={{ marginLeft: 6, color: '#1d4ed8', fontWeight: 700 }}>· {otStops.length}</span>
                    </Typography>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 165, overflowY: 'auto' }}>
                      {otStops.map((s, i) => (
                        <Box key={s.ot.otId || i} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 9px', background: '#fff',
                          border: '1px solid #e2e8f0', borderRadius: 8,
                        }}>
                          <Box style={{
                            width: 22, height: 22, borderRadius: 8,
                            background: s.kind === 'destination' ? '#fee2e2' : '#dbeafe',
                            color: s.kind === 'destination' ? '#dc2626' : '#1d4ed8',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.66rem', fontWeight: 800, flexShrink: 0,
                          }}>{s.order}</Box>
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                              {s.ot.customRef || s.ot.orderNumber || `OT-${s.ot.otId}`}
                            </Typography>
                            <Typography style={{ fontSize: '0.66rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>
                              {s.ot.name || '—'}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()}

              {/* Stock na viatura */}
              {route?.vehicleId && (
                <Box style={{ padding: '10px 12px', background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                  <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                    {lang === 'pt' ? 'Stock na viatura' : 'Vehicle stock'}
                    {!vehicleStockLoading && vehicleStock.length > 0 && (
                      <span style={{ marginLeft: 6, color: '#1d4ed8', fontWeight: 700 }}>· {vehicleStock.length}</span>
                    )}
                  </Typography>
                  {vehicleStockLoading ? (
                    <Typography style={{ fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic' }}>
                      {lang === 'pt' ? 'A carregar…' : 'Loading…'}
                    </Typography>
                  ) : vehicleStock.length === 0 ? (
                    <Box style={{ padding: '8px 10px', border: '1px dashed #e2e8f0', borderRadius: 8, background: '#fff' }}>
                      <Typography style={{ fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic' }}>
                        {vehicleStockShadow
                          ? (lang === 'pt' ? 'Viatura ainda sem stock atribuído.' : 'Vehicle has no stock yet.')
                          : (lang === 'pt' ? 'Sem stock na viatura.' : 'No stock on this vehicle.')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box style={{ maxHeight: 165, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
                      {vehicleStock.map((s, i) => (
                        <Box key={s.stockItemId} style={{
                          display: 'grid', gridTemplateColumns: '1fr auto',
                          gap: 8, padding: '6px 10px',
                          borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
                          alignItems: 'center',
                        }}>
                          <Box style={{ minWidth: 0 }}>
                            <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>
                              {s.name}
                            </Typography>
                            <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', lineHeight: 1.2 }}>
                              {s.code}{s.category ? ` · ${s.category}` : ''}
                            </Typography>
                          </Box>
                          <Box style={{
                            padding: '2px 8px', background: '#dbeafe', borderRadius: 999,
                            fontSize: '0.72rem', fontWeight: 800, color: '#1d4ed8',
                          }}>
                            {Number(s.quantity).toLocaleString('pt-PT', { maximumFractionDigits: 3 })}<span style={{ marginLeft: 3, fontSize: '0.6rem', color: '#64748b', fontWeight: 600 }}>{s.unit}</span>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          <Box style={{ padding: '10px 18px', borderTop: '1px solid #e2e8f0', background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Typography style={{ flex: 1, fontSize: '0.66rem', color: '#94a3b8', fontStyle: 'italic' }}>
              {lang === 'pt'
                ? '🔔 Ao confirmar, a rota é gravada e o motorista recebe notificação push imediata + PDF.'
                : '🔔 On confirm, the route is saved and the driver gets a push notification + PDF.'}
            </Typography>
            <Button
              onClick={() => setDispatchConfirmOpen(false)}
              disabled={dispatchInProgress}
              style={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}
            >
              {lang === 'pt' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (dispatchInProgress) return;
                setDispatchInProgress(true);
                try {
                  // Save → clear → navigate. Espelha handleSaveRoute mas
                  // adiciona a limpeza do mapa + navegação às Ordens.
                  const finalName = route.name || 'Rota sem nome';
                  dispatch(setRouteName(finalName));
                  const routeToSave = { ...route, name: finalName };
                  const result = await dispatch(saveRouteToAPI({ token, route: routeToSave, dispatch: true }));
                  if (result.error) {
                    dispatch(addToast({ type: 'error', message: 'Erro ao despachar a rota' }));
                    setDispatchInProgress(false);
                    return;
                  }
                  const otCount = ((route.waypoints || []).filter(w => w?.otId).length) + (route.destination?.otId ? 1 : 0);
                  dispatch(addToast({
                    type: 'success', severity: 'success',
                    message: lang === 'pt'
                      ? `📱 Rota despachada para ${route.driverName || 'motorista'} · ${otCount} ${otCount === 1 ? 'ordem' : 'ordens'}`
                      : `📱 Route dispatched to ${route.driverName || 'driver'} · ${otCount} order${otCount === 1 ? '' : 's'}`,
                    duration: 6000,
                  }));
                  setDispatchConfirmOpen(false);
                  setDispatchInProgress(false);
                  dispatch(clearRoute());
                  dispatch(navigateTo('orders'));
                } catch (e) {
                  console.error('[Dispatch] error:', e);
                  dispatch(addToast({ type: 'error', message: 'Erro: ' + (e?.message || e) }));
                  setDispatchInProgress(false);
                }
              }}
              disabled={dispatchInProgress}
              startIcon={dispatchInProgress
                ? <HourglassEmptyIcon style={{ fontSize: 16, animation: 'spin 1.4s ease-in-out infinite' }} />
                : <SendIcon style={{ fontSize: 16 }} />}
              style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 700,
                padding: '7px 18px',
                boxShadow: '0 2px 6px rgba(245,158,11,0.35)',
              }}
            >
              {dispatchInProgress
                ? (lang === 'pt' ? 'A despachar...' : 'Dispatching...')
                : (lang === 'pt' ? 'Confirmar e Despachar' : 'Confirm & Dispatch')}
            </Button>
          </Box>
        </Dialog>,
        document.body
      )}
    </Box>
  );
}

